import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import { useChunkUpload } from 'hooks/useChunkUpload';
import i18next from 'i18next';
import { IVideoResponse } from 'services/video/types';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ITrackFilePath,
  ITrackFileUploadComplete,
  ITrackMetadataResponse,
  ITrackResponse,
} from './types';

export const usePostTrackFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, success, dismiss } = useQueryToast('usePostTrackFile');
  return useMutation<ITrackFilePath, DefaultError, { file: File; trackId: string }>({
    mutationFn: async ({ file, trackId }) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('track.file-what'),
        }),
      );

      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient({
        method: 'POST',
        url: 'Track/File',
        body: formData,
        params: { trackId },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `usePostTrackFile - ${trackId}`,
      });
    },
    onSuccess: (data) =>
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('track.file-what'),
        }),
      ),
    onError: dismiss,
  });
};

export const usePutTrackFile = (trackId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutTrackFile');
  return useMutation<ITrackFilePath, DefaultError, File>({
    mutationFn: async (file) => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('track.file-what'),
        }),
      );

      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient({
        method: 'PUT',
        url: 'Track/File',
        body: formData,
        params: { trackId },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `usePutTrackFile - ${trackId}`,
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('track.file-what'),
        }),
      );
    },
    onError: dismiss,
  });
};

export const usePostTrackChunkedFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostTrackChunkedFile');

  const { uploadChunkedFiles } = useChunkUpload('Post Track');

  return useMutation<ITrackResponse, DefaultError, ITrackFileUploadComplete>({
    mutationFn: async ({ file, trackId, version }) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('track.file-what'),
        }),
      );

      const { totalChunks, importId } = await uploadChunkedFiles(file, undefined);

      const finalResponse = await apiClient({
        method: 'POST',
        url: 'Track/File/UploadComplete',
        params: { importId, totalChunks, trackId, version },
      });

      return responseHandler(finalResponse, {
        groupName: 'usePostTrackChunkedFile',
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('track.file-what'),
        }),
      );
    },
    onError: dismiss,
  });
};

export const usePutTrackChunkedFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutTrackChunkedFile');

  const { progress, resetProgress, uploadChunkedFiles } = useChunkUpload('Put Track');

  return useMutation<ITrackResponse, DefaultError, ITrackFileUploadComplete>({
    mutationFn: async ({ file, trackId, version }) => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('track.file-what'),
        }),
      );

      const { totalChunks, importId } = await uploadChunkedFiles(file, undefined);

      const finalResponse = await apiClient({
        method: 'PUT',
        url: 'Track/File/UpdateComplete',
        params: { importId, totalChunks, trackId, version },
      });

      return responseHandler(finalResponse, {
        groupName: 'usePutTrackChunkedFile',
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('track.file-what'),
        }),
      );
    },
    onError: dismiss,
  });
};

export const useDeleteTrackFile = (trackId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteTrackFile');
  return useMutation<IVideoResponse, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('track.file-what'),
        }),
      );

      const response = await apiClient({
        method: 'DELETE',
        url: 'Track/File',
        params: { trackId },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `useDeleteTrackFile - ${trackId}`,
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('track.file-what'),
        }),
      );
    },
    onError: dismiss,
  });
};

export const useGetTrackFileMetadata = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITrackMetadataResponse, DefaultError>({
    queryKey: ['track-metadata', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Track/${id}/Metadata`,
      });

      return responseHandler(response, {
        groupName: `useGetTrackFileMetadata - ${id}`,
        notFoundPath: '/track',
      });
    },
    enabled: enabled,
  });
};
